import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook for reading and setting the section index via the URL query params.
 * The URL is the single source of truth, so the browser back/forward buttons
 * step through the sections. The `section` param is 1-indexed.
 */
const useSectionIndex = (): [number, React.Dispatch<React.SetStateAction<number>>] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const sectionParam = searchParams.get("section");

    const numeric = !!sectionParam && /^\d+$/.test(sectionParam);
    const parsed = numeric ? parseInt(sectionParam, 10) : NaN;
    const isIndex = parsed >= 1;
    const valid = isIndex || (!!sectionParam && !numeric);
    const sectionIndex = isIndex ? parsed - 1 : 0;

    useEffect(() => {
        if (!valid) {
            setSearchParams(
                (prev) => {
                    prev.set("section", "1");
                    return prev;
                },
                { replace: true }
            );
        }
    }, [valid, setSearchParams]);

    const setSectionIndex: React.Dispatch<React.SetStateAction<number>> = (value) => {
        const next = typeof value === "function" ? value(sectionIndex) : value;
        setSearchParams((prev) => {
            prev.set("section", (next + 1).toString());
            prev.delete("question");
            return prev;
        });
        window.scrollTo(0, 0);
    };

    return [sectionIndex, setSectionIndex];
};

export default useSectionIndex;
